/**
 * ZLRs 法则轮换 —— 本作招牌机制。
 *
 * 来源：01_核心设计/主设计方案.md §7
 *
 * 设计约束（数值平衡文档 §5.1）：
 *   任何法则对单一数值的修正幅度不得超过 ±35%，且不得同时修正超过 3 个维度。
 *   法则改变的是「什么兵种此刻更划算」，不是「谁能赢」。
 *   唯一例外是引力异常对飞行单位的质变（本原型暂无空军，故未实装）。
 *
 * 所有修正量以千分比表示：1000 = 无变化，1200 = +20%，800 = −20%。
 */

import { Kind, TICKS_PER_SEC } from '../../shared/constants.ts';

export interface LawDef {
  id: number;
  code: string;
  name: string;
  desc: string;
  /** UI 与全屏滤镜用色 */
  color: number;
  /** 抽取权重（千分比相对值）。0 表示需要特殊条件才会进入池子。 */
  weight: number;
  /** 生效的最早比赛时间（秒）。反僵局法则只在中后期出现。 */
  minTime: number;
  /** 是否会自动轮换。ZLR-15 玄镜凝视不会自动结束。 */
  autoRotate: boolean;

  // ── 全局修正 ──
  /** 移速修正 */
  speedMul: number;
  /** 步兵额外移速修正（叠乘在 speedMul 之上） */
  infantrySpeedMul: number;
  /** 载具额外移速修正 */
  vehicleSpeedMul: number;
  /** 伤害修正 */
  damageMul: number;
  /** 载具/机械单位额外伤害修正 */
  vehicleDamageMul: number;
  /** 受到的伤害修正（<1000 为减伤） */
  damageTakenMul: number;
  /** 建筑受到的伤害修正 */
  structureDamageTakenMul: number;
  /** 建造与生产速度修正 */
  buildSpeedMul: number;
  /** 射程修正 */
  rangeMul: number;
  /** 视野修正 */
  sightMul: number;
  /** Intel 产出修正 */
  intelMul: number;
  /** 治疗/维修效率修正 */
  repairMul: number;

  // ── 周期效应 ──
  /** 每秒对所有单位造成的伤害（法则伤害，无视护甲矩阵） */
  dotPerSec: number;
  /** dotPerSec 是否只影响步兵（辐射风暴：载具内乘员免疫） */
  dotInfantryOnly: boolean;
  /** 每秒回复的最大生命千分比 */
  regenPermille: number;
  /** 防御建筑间歇断电：每 N 秒断电 M 秒，0 表示无 */
  towerBlackoutPeriod: number;
  towerBlackoutDuration: number;
  /** 雷达与小地图失效 */
  radarBlackout: boolean;
}

function law(d: Partial<LawDef> & Pick<LawDef, 'id' | 'code' | 'name' | 'desc' | 'color'>): LawDef {
  return {
    weight: 1000,
    minTime: 0,
    autoRotate: true,
    speedMul: 1000,
    infantrySpeedMul: 1000,
    vehicleSpeedMul: 1000,
    damageMul: 1000,
    vehicleDamageMul: 1000,
    damageTakenMul: 1000,
    structureDamageTakenMul: 1000,
    buildSpeedMul: 1000,
    rangeMul: 1000,
    sightMul: 1000,
    intelMul: 1000,
    repairMul: 1000,
    dotPerSec: 0,
    dotInfantryOnly: false,
    regenPermille: 0,
    towerBlackoutPeriod: 0,
    towerBlackoutDuration: 0,
    radarBlackout: false,
    ...d,
  };
}

/** id 0 = 常态物理，开局前 2 分钟生效 */
export const LAWS: LawDef[] = [
  law({
    id: 0,
    code: 'NORM',
    name: '常态物理',
    desc: '法则尚未波动，一切按旧世界的规则运行。',
    color: 0x8a9aa8,
    weight: 0,
  }),
  law({
    id: 1,
    code: 'ZLR-01',
    name: '静滞力场',
    desc: '时空变得粘稠。全体移速 −25%、建造 −20%，但射程 +10%。',
    color: 0x7f8fa6,
    weight: 1500,
    speedMul: 750,
    buildSpeedMul: 800,
    rangeMul: 1100,
  }),
  law({
    id: 2,
    code: 'ZLR-02',
    name: '辐射风暴',
    desc: '露天步兵持续受伤，载具乘员免疫。全体视野 −30%。',
    color: 0x6fbf5a,
    weight: 1500,
    dotPerSec: 2,
    dotInfantryOnly: true,
    sightMul: 700,
  }),
  law({
    id: 3,
    code: 'ZLR-03',
    name: '引力异常',
    desc: '载具移速 −20%，步兵移速 +10%。飞行单位强制迫降。',
    color: 0xa66fbf,
    weight: 1000,
    vehicleSpeedMul: 800,
    infantrySpeedMul: 1100,
  }),
  law({
    id: 4,
    code: 'ZLR-04',
    name: '信息湍流',
    desc: '雷达与小地图失效，Intel 产出归零。',
    color: 0xbf6f6f,
    weight: 800,
    radarBlackout: true,
    intelMul: 0,
  }),
  law({
    id: 5,
    code: 'ZLR-05',
    name: '量子静默',
    desc: '宇宙屏住了呼吸。全体受到伤害 −20%，建筑额外 −30%。',
    color: 0x6f9fbf,
    weight: 1500,
    damageTakenMul: 800,
    structureDamageTakenMul: 700,
  }),
  law({
    id: 6,
    code: 'ZLR-06',
    name: '时间褶皱',
    desc: '建造与生产速度 +30%，全体缓慢回复生命。',
    color: 0xbfae6f,
    weight: 1500,
    buildSpeedMul: 1300,
    regenPermille: 5,
  }),
  law({
    id: 7,
    code: 'ZLR-07',
    name: '虚空回响',
    desc: '虚空在聆听。Intel 产出 +30%，但全体伤害 −10%。',
    color: 0x9b6fbf,
    weight: 1000,
    intelMul: 1300,
    damageMul: 900,
  }),
  law({
    id: 8,
    code: 'ZLR-08',
    name: '法则崩塌',
    desc: '⚠ 现实结构正在解离。全体持续受伤，治疗减半，建造受阻。',
    color: 0xd94f4f,
    weight: 500,
    minTime: 480,
    dotPerSec: 3,
    repairMul: 500,
    buildSpeedMul: 850,
  }),
  law({
    id: 9,
    code: 'ZLR-09',
    name: '电磁风暴',
    desc: '载具伤害 −30%，防御塔间歇断电，步兵移速 +15%。',
    color: 0x6fbfbf,
    weight: 1000,
    vehicleDamageMul: 700,
    infantrySpeedMul: 1150,
    towerBlackoutPeriod: 8,
    towerBlackoutDuration: 2,
  }),
  law({
    id: 10,
    code: 'ZLR-10',
    name: '因果逆流',
    desc: '结果先于原因出现。全体伤害 −15%，但射程 +15%。',
    color: 0xbf8f6f,
    weight: 1000,
    damageMul: 850,
    rangeMul: 1150,
  }),
  law({
    id: 11,
    code: 'ZLR-11',
    name: '熵增加速',
    desc: '物质在眼前解体。全体持续受伤，一切治疗与维修减半。',
    color: 0xbf6f4f,
    weight: 800,
    dotPerSec: 2,
    repairMul: 500,
  }),
  law({
    id: 12,
    code: 'ZLR-12',
    name: '维度薄膜',
    desc: '高维边界模糊。全体移速 +25%、视野 +20%。',
    color: 0x6f7fbf,
    weight: 800,
    speedMul: 1250,
    sightMul: 1200,
  }),
  law({
    id: 13,
    code: 'ZLR-13',
    name: '生命涌流',
    desc: '未知能量场激发了生物活性。全体每秒回复生命，维修效率 +30%。',
    color: 0x7fbf6f,
    weight: 1500,
    regenPermille: 8,
    repairMul: 1300,
  }),
  law({
    id: 14,
    code: 'ZLR-14',
    name: '记忆风暴',
    desc: '逝者的记忆化作实体。Intel 产出 +20%，全体视野 +25%。',
    color: 0xbf9fbf,
    weight: 1000,
    intelMul: 1200,
    sightMul: 1250,
  }),
  law({
    id: 15,
    code: 'ZLR-15',
    name: '玄镜凝视',
    desc: '★ 某种超越时空的存在正注视着战场。全体伤害 +25%，Intel +50%。此法则不会自动结束。',
    color: 0xd9c04f,
    weight: 0,
    minTime: 1200,
    autoRotate: false,
    damageMul: 1250,
    intelMul: 1500,
  }),
];

/** 法则轮换周期（秒） */
export const LAW_PERIOD_SEC = 240;
/** 开局的常态物理持续时间（秒） */
export const LAW_GRACE_SEC = 120;
/** 轮换前的预警时间（秒） */
export const LAW_WARN_SEC = 30;

export const LAW_PERIOD_TICKS = LAW_PERIOD_SEC * TICKS_PER_SEC;
export const LAW_GRACE_TICKS = LAW_GRACE_SEC * TICKS_PER_SEC;
export const LAW_WARN_TICKS = LAW_WARN_SEC * TICKS_PER_SEC;

/** 该法则的移速修正是否适用于此实体类型 */
export function lawSpeedMul(law: LawDef, kind: Kind): number {
  let m = law.speedMul;
  if (kind === Kind.Infantry) m = (m * law.infantrySpeedMul) / 1000;
  else if (kind === Kind.Vehicle || kind === Kind.Harvester) m = (m * law.vehicleSpeedMul) / 1000;
  return m | 0;
}

export function lawDamageMul(law: LawDef, kind: Kind): number {
  let m = law.damageMul;
  if (kind === Kind.Vehicle || kind === Kind.Building) m = (m * law.vehicleDamageMul) / 1000;
  return m | 0;
}
